import type { modelState, ModelStateList } from "@/utils/modelState";
import type { SaveProgressInfo } from "./saveProgress";
import type { ProgramBookingState, ReservationProgramBookingDiff, ReservationProgramBookingDiffDto } from "./model.Programs";
import type { RatesRatePlanOverrides } from "./Rates/CustomRatePlan";
import type { RatesBaseRateOverrides } from "./Rates/CustomBaseRate";
import type { TaxDocumentTypes } from "./TaxDocumentTypes/TaxDocumentType";
import type { PaymentTypes } from "./PaymentTypes/PaymentType";
import type { RefundTypes } from "./RefundTypes/RefundType";
import type { ReservationStatus } from "./ReservationStatus";
import type { ReservationQuoteStatuses } from "./ReservationQuoteStatuses/ReservationQuoteStatus";
import type { ReservationLogEntry } from "./ReservationLogEntry";
import type { ReservationArrivalTransferMode } from "./ReservationArrivalTransferMode";
import type { ReservationDepartureTransferMode } from "./ReservationDepartureTransferMode";
import type { ReservationLogEntryDto } from "./ReservationLogEntry";
import type { ReservationPersonCache } from "./model.ReservationPerson";
import type { ReservationCompanyCache } from "./model.ReservationCompany";
import type { ReactNode } from 'react';

import type { AfterProfileSlotProps, AfterProfileSlotRenderFn, MacroConfiguration, ReservationState, MergeableRoomCluster, ReservationPerson, ReservationNewPerson, isReservationNewPerson, ReservationExistingPerson, LoadProfileResult, PersonCredit, RoomBookingState, AvailabilityCheckResult, ProgramAvailabilityCheckResult, RoomBooking, RoomPrice, PriceWithDiscount, RoomBlockDateDto, RoomBlockDate, RoomBlockDateRoom, CalculatedPrice, CalculatedRowPrice, RoomBookingDate, RoomBookingDateDto, RoomBookingDateVoucher, RoomBookingDateVoucherEffect, PriceBreakdownContainer, SplitAmountsContainer, AmountWithSource, MultiplierWithSource, RoomBookingPerson, Voucher, SaveReservationRequestVoucher, ExternalPostingStatus, QbJournalLink, ReservationPayment, ReservationPaymentTransaction, ReservationRefund, ReservationRefundTransaction, RevenueItem, ReservationPosItem, ReservationQuote, ReservationNote, ReservationLogEntryContainer, ArrivalTransferDetailsState, DepartureTransferDetailsState, NewPersonDto, NewCompanyDto, PersonDto, CompanyDto, ReservationCompany, ReservationNewCompany, isReservationNewCompany, ReservationExistingCompany, isReservationExistingCompany, ReservationContact, RoomType, RoomFormFieldRefDto, RoomTypeRoom, ProgramType, OccupantType, RoomBlockDatesContainer, RatesContainerDto, RatesRatePlanDto, RatePlanFormFieldDto, RatePlanFormFieldOptionDto, RatePlanFormResponse, RatePlanFieldValue, RatesBaseRateDto, RatesRateMultiplierDto, RatesRateDiscountDto, RatePlanKey } from "./types";

export interface ReservationDiffContainer {
    Rooms: ReservationRoomBookingDiff[];
    Programs: ReservationProgramBookingDiff[];
    PosItems: ReservationPosItemDiff[];
    Transactions: DiffTransaction[];
    Versions: number[];
    IsNew: boolean;
}

export interface RoomSplitInfo {
    mode: ReservationItemSplitMode;
    splitFromReservationRoomId: string | null;
}

export interface ReservationRoomBookingDiff {
    RoomBooking: RoomBookingState;
    Action: 'None' | 'Added' | 'Removed' | 'Updated';
}

export interface ReservationPosItemDiff {
    PosItem: ReservationPosItem;
    Action: 'None' | 'Added' | 'Removed' | 'Updated';
}

export interface DiffTransaction {
    Description: string;
    Amount: number;
}

export interface ReservationPosItemDto {
    Description: string;
    Quantity: number;
    Amount: number;
    ReservationOrderId: string;
    ReservationOrderLineItemId: string;
    RevenueDate: string;
    CanRemove: boolean;
    CanChangeAmount: boolean;
    IsPackage: boolean;
    // Server-computed revenue lock verdict (lock-date boundary OR closed date).
    IsRevenueLocked: boolean;
}


// Program split modes for duration and discount handling
export type ReservationItemSplitMode = "Disconnected" | "Combined" | "Extended";


// Program split modes for duration and discount handling
export type ProgramSplitMode = "Disconnected" | "Combined" | "Extended";

// Room split modes for duration and discount handling
export type RoomSplitMode = "Disconnected" | "Combined" | "Extended";

export interface CheckPriceResult {
    rooms: CheckPriceResultRoom[];
    programs: CheckPriceResultProgram[];
    orderDiffs: ReservationDiffContainerDto[];
}


export interface CheckPriceResultRoom {
    reservationRoomId: string;
    price: PriceWithDiscount;
    dates: RoomBookingDateDto[];
    roomBlocks: RoomBlockDateDto[];
    roomMovements: number;
    availability: AvailabilityCheckResultDto;
    additionalCharges: PriceBreakdownContainer[];
}

export interface AvailabilityCheckResultDto {
    isAvailable: boolean;
    isBlockedByTempReservation: boolean;
    blockedByTempReservationNumbers: number[];
}

export interface CheckPriceResultProgram {
    reservationProgramId: string;
    price: PriceWithDiscount;
    dates: RoomBookingDateDto[];
    availability: ProgramAvailabilityCheckResultDto;
}

export interface ProgramAvailabilityCheckResultDto {
    isAvailable: boolean;
    isBlockedByTempReservation: boolean;
    blockedByTempReservationNumbers: number[];
}

export interface ReservationDiffContainer {
    Rooms: ReservationRoomBookingDiff[];
    Programs: ReservationProgramBookingDiff[];
    PosItems: ReservationPosItemDiff[];
    Transactions: DiffTransaction[];
    Versions: number[];
    IsNew: boolean;
}

export interface ReservationDiffContainerDto {
    Rooms: ReservationRoomBookingDiffDto[];
    Programs: ReservationProgramBookingDiffDto[];
    PosItems: ReservationPosItemDiffDto[];
    Transactions: DiffTransaction[];
    Versions: number[];
    IsNew: boolean;
}

export interface ReservationRoomBookingDiffDto {
    RoomBooking: RoomBooking;
    Action: 'None' | 'Added' | 'Removed' | 'Updated';
}

export interface ReservationRoomBookingDiff {
    RoomBooking: RoomBookingState;
    Action: 'None' | 'Added' | 'Removed' | 'Updated';
}

export interface ReservationPosItemDiffDto {
    PosItem: ReservationPosItemDto;
    Action: 'None' | 'Added' | 'Removed' | 'Updated';
}

export interface ReservationPosItemDiff {
    PosItem: ReservationPosItem;
    Action: 'None' | 'Added' | 'Removed' | 'Updated';
}


export interface ReservationLogEntryContainerDto {
    LogEntryId: string;
    LogText: string;
    LogData: ReservationLogEntryDto;
    CreatedBy: string;
    CreatedAt: Date;
}

export interface ReservationRefundDto {
    CreatedDate: string;
    RefundId: string;
    RefundType: RefundTypes.RefundTypeDto;
    Amount: number;
    Transactions: ReservationRefundTransactionDto[];
}

export interface ReservationRefundTransactionDto {
    CreatedDate: string;
    EffectiveDate: Date;
    Amount: number;
    Note: string;
    TaxDocument: TaxDocumentTypes.TaxDocumentTypeDto;
    IsVoid?: boolean;
    // See ReservationPaymentTransaction.JournalDocumentNumber.
    JournalDocumentNumber?: string | null;
    ExternalPostingStatus?: ExternalPostingStatus | null;
}

export interface ReservationPaymentDto {
    CreatedDate: string;
    PaymentId: string;
    PaymentType: PaymentTypes.PaymentTypeDto;
    Amount: number;
    Transactions: ReservationPaymentTransactionDto[];
}

export interface ReservationPaymentTransactionDto {
    CreatedDate: string;
    EffectiveDate: Date;
    Amount: number;
    Note: string;
    TaxDocument: TaxDocumentTypes.TaxDocumentTypeDto;
    IsVoid?: boolean;
    // Document whose journal this row maps to: {base}-V{seq} for a void/restore toggle, else the base
    // TN/CN. The join key for this row's own posting status + the manual-post target. Null when no doc.
    JournalDocumentNumber?: string | null;
    ExternalPostingStatus?: ExternalPostingStatus | null;
}

export enum ReservationStatusEnum {
    Quote = "Quote",
    Reserved = "Reserved",
    Released = "Released"
}

export interface VersionHistoryResponse {
    versions: VersionSummary[];
}

export interface VersionSummary {
    version: number;
    createdUtc: string;
    createdByUserId: string;
    createdByUserName: string | null;
    status: string;
    rooms: VersionRoomSummary[];
    programs: VersionProgramSummary[];
    arrivalDate: string | null;
    departureDate: string | null;
    totalAmountMinor: number;
    totalBaseAmountMinor: number;
    isCurrentVersion: boolean;
    diff: ReservationDiffContainer | null;
    roomAvailability: VersionRoomAvailability[];
    programAvailability: VersionProgramAvailability[];
}

export interface VersionRoomSummary {
    roomTypeKey: string;
    roomLocationId: string | null;
    checkIn: string;
    checkOut: string;
    breakdowns: PriceBreakdownContainer[];
    totalBaseAmount: number;
    totalPayableAmount: number;
}

export interface VersionProgramSummary {
    programTypeKey: string;
    start: string;
    end: string;
    breakdowns: PriceBreakdownContainer[];
    totalBaseAmount: number;
    totalPayableAmount: number;
}

export interface VersionRoomAvailability {
    roomTypeKey: string;
    checkIn: string;
    checkOut: string;
    isAvailable: boolean;
    isNoShow: boolean;
    isBlockedByTempReservation: boolean;
    blockedByTempReservationNumbers: number[];
}

export interface VersionProgramAvailability {
    programTypeKey: string;
    start: string;
    end: string;
    isAvailable: boolean;
    isNoShow: boolean;
    isBlockedByTempReservation: boolean;
    blockedByTempReservationNumbers: number[];
}
